import { Grid, Table, TableBody, TableRow } from '@material-ui/core'
import moment, { Moment } from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { SalaryCalculationItem } from '../../../api/salaryCalculation'
import { payoutDateFormat } from '../../../shared/constants/dateFormats'
import { getCompensationCellProps } from '../utils/getCompensationCellProps'
import CompensationCell from './CompensationCell'
import CornerTitlesCell from './CornerTitlesCell'
import DateCell from './DateCell'
import SumCell from './SumCell'

type SalaryCalculationTableProps = {
  activeRevenueRow?: string
  salaryCalculationItems: SalaryCalculationItem[]
  payoutDateScale: Moment[]
  revenueDateScale: Moment[]
  getRevenueColumnClickHandler: (
    revenueDate: string,
    currentCompensation?: number,
    isInitialCompensation?: boolean
  ) => (event: React.MouseEvent<HTMLTableCellElement>) => void
}

const SalaryCalculationTable = ({
  salaryCalculationItems,
  activeRevenueRow,
  payoutDateScale,
  revenueDateScale,
  getRevenueColumnClickHandler,
}: SalaryCalculationTableProps) => {
  const { t } = useTranslation()

  return (
    <Grid item>
      <Table>
        <TableBody>
          <TableRow>
            <CornerTitlesCell
              colSpan={2}
              rowSpan={2}
              topTitle={t('salary_table_horizontal_header_label')}
              topTooltipText={t('salary_table_horizontal_header_tooltip_content')}
              bottomTitle={t('salary_table_vertical_header_label')}
              bottomTooltipText={t('salary_table_vertical_header_tooltip_content')}
            />
            {payoutDateScale?.map((payoutDate, index) => {
              const payoutDateStr = payoutDate.format(payoutDateFormat)
              const isLastCol = index + 1 === payoutDateScale.length
              const isFirstCol = index === 0

              return (
                <DateCell variant="salary" isLast={isLastCol} isFirst={isFirstCol} key={`payout-date-${payoutDateStr}`}>
                  {payoutDateStr}
                </DateCell>
              )
            })}
          </TableRow>
          <TableRow>
            {payoutDateScale?.map((payoutDate, index) => {
              const payoutMonth = payoutDate.get('month')
              const isLastCol = index + 1 === payoutDateScale.length

              const payoutItem = salaryCalculationItems?.find((salaryCalcItem) => {
                const salaryCalcItemMonth = moment(salaryCalcItem.date).add(1, 'month').get('month')
                return payoutMonth === salaryCalcItemMonth
              })

              return (
                <SumCell
                  sum={payoutItem?.totalPayout as number}
                  variant="salary"
                  isLast={isLastCol}
                  key={`header-amount-${payoutMonth}`}
                />
              )
            })}
          </TableRow>
          {revenueDateScale?.map((revenueDate, index) => {
            const revenueDateStr = revenueDate.format(payoutDateFormat)
            const revenueItem = salaryCalculationItems?.find((salaryCalcItem) =>
              revenueDate.isSame(moment(salaryCalcItem.date), 'month')
            )
            const isLastRow = index + 1 === revenueDateScale.length
            const isFirstRow = index === 0

            return (
              <TableRow key={`row-${revenueDateStr}`}>
                <DateCell variant="revenue" isLast={isLastRow} isFirst={isFirstRow}>
                  {revenueDateStr}
                </DateCell>
                <SumCell
                  sum={revenueItem?.currentCompensation}
                  variant="revenue"
                  isLast={isLastRow}
                  isActive={activeRevenueRow === revenueDateStr}
                  onClick={getRevenueColumnClickHandler(revenueDateStr, revenueItem?.currentCompensation)}
                />
                {payoutDateScale.map((payoutDate) => {
                  const { compensation, variant } = getCompensationCellProps(payoutDate, revenueItem)

                  return (
                    <CompensationCell
                      compensation={compensation}
                      variant={variant}
                      key={`cell-${payoutDate.format(payoutDateFormat)}`}
                    />
                  )
                })}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Grid>
  )
}

export default SalaryCalculationTable
